import env from "environment"
import { config } from "./Configuration";
import { executeAsync } from "./DataService";
import { isNativeApp, nativeApp } from "./NativePayment";
import { apiClient } from "./PmApiClient";
import {
    UserAccountType,
    type Guid,
    type IOpenSession,
    type IOpenSessionResult,
    UserSessionStatus
} from "../entities/Commands";
import { AccessPermission, type ResourceName, type ResourceScope } from "../entities/Types";

export enum SessionValidation {
    Valid,
    Invalid,
}


interface IParsedPermissions {
    grants: AccessPermission[];
    scopes: Record<string, string[]>;
}

interface IActiveSession extends IOpenSessionResult {
    walletId?: Guid;
}

class UserSession {

    protected _active: IActiveSession;
    protected _lastValidationTime: number;
    protected _sessionSaved: boolean;
    protected _permissions: IParsedPermissions;
    protected _isDeviceValid: boolean;

    constructor() {

        if (!this.deviceId && !isNativeApp())
            this.deviceId = crypto.randomUUID();

        let sessionJson = localStorage.getItem("session");

        if (!sessionJson) {
            this._sessionSaved = false;
            sessionJson = sessionStorage.getItem("session");
        }
        else
            this._sessionSaved = true;

        if (sessionJson)
            this.setActiveSession(JSON.parse(sessionJson), this._sessionSaved);

        window.addEventListener("beforeunload", () => {

            console.log("beforeunload");

            if (!this.active?.sessionId || this._sessionSaved)
                return;

            this.closeAsync();
        });
    }

    async closeAsync(useBacon = false, onlyUnsaved = false) {

        if (!this.active?.sessionId)
            return;

        if (onlyUnsaved && this._sessionSaved)
            return;

        if (useBacon) {

            const uri = apiClient.getCommandUrl("CloseSession") + "&auth=" + encodeURIComponent(this.active.accessToken);

            if ("navigator" in window) {
                console.log("sending beacon")
                navigator.sendBeacon(uri, JSON.stringify({ sessionId: this.active?.sessionId }));
            }

        }
        else
            await executeAsync("CloseSession", { sessionId: this.active?.sessionId });

        this.invalidate();
    }

    async openAsync(data: Partial<IOpenSession>, save: boolean) {

        const result = (await executeAsync("OpenSession", {
            ...data,
            deviceSerial: isNativeApp() ? nativeApp.getDeviceInfo().serial : undefined,
            neverExpires: save
        }, {
            onError: err => {
                if (data.accessRequestId)
                    throw err;
                return false;
            }
        })) as IActiveSession;

        this.invalidate();

        if (result)
            result.walletId = data.walletId;

        this.setActiveSession(result, save);

        if (result.accessToken && result.sessionStatus !== UserSessionStatus.Pending)
            await this.updatePermissionsAsync(true);

        return result;
    }
    
    async sendOtpAsync() {
        return await executeAsync(
          "SendOtp", 
          {}
      );
    }
    
    async validateOtpAsync(otp: string) {
        const result = await executeAsync(
          "ValidateOtp", 
          { otp: otp }
        );
        
        if(!result)
            return false
        
        this._active.sessionStatus = result.sessionStatus;
        this.setActiveSession(this.active, this._sessionSaved)
        
        await this.validateAsync();
        
        return true;
    }

    get isBusiness() {
        return this.active?.user?.accountType == UserAccountType.Business;
    }

    hasPermission(res: ResourceName, scope: ResourceScope, resId: string = undefined) {

        if (!this._permissions?.grants)
            return true;

        for (const grant of this._permissions?.grants) {

            if (res && grant.resource && grant.resource !== res)
                continue;

            if (resId && grant.resourceId && grant.resourceId !== resId)
                continue;   

            if (scope && grant.scope) {

                if (!this._permissions.scopes[grant.scope].includes(scope))
                    continue;
            }

            return true;    
        }

        return false;   
    }


    async isDeviceValidAsync() {

        if (!isNativeApp() || this._isDeviceValid === true)
            return true;

        /*
        TODO uncomment for future releases
        if (this.active?.walletId || this.active?.user?.accountType == UserAccountType.Personal)
            return true;
        */

        let result = this.deviceId != null;

        if (result) {
            result = await executeAsync("CheckDeviceRegistration", {
                id: this.deviceId,
                serial: isNativeApp() ? nativeApp.getDeviceInfo().serial : undefined
            }, { method: "GET" });
        }

        this._isDeviceValid = result;

        return result;
    }

    invalidate() {

        sessionStorage.removeItem("session");

        localStorage.removeItem("session");

        localStorage.removeItem("pm.context");

        this._active = undefined;

        this._permissions = undefined;

        this._isDeviceValid = undefined;

        this.setActiveSession(undefined);
    }
    

    async validateAsync() :Promise<SessionValidation> {

        if (!this.active)
            return SessionValidation.Invalid;

        const res = await executeAsync("ValidateSession", {
            nativeVersion: isNativeApp() ? nativeApp.getVersion() : undefined,
            webVersion: {
                versionName: config.current.version,
                environment: env
            }
        }, {
            method: "GET",
            onError: (error: Error) => {
                if (error.message == "err-unauthorized")
                    return true;
            }
        });

        if (!res?.isValid)
            return SessionValidation.Invalid;

        this._lastValidationTime = Date.now();

        await this.updatePermissionsAsync();

        return SessionValidation.Valid;
    }

    protected async updatePermissionsAsync(force = false) {

        if (this._permissions && !force)
            return;

        this._permissions = undefined;  

        const perms = await executeAsync("GetPermissions");

        this._permissions = {
            grants: perms.grants.map(g => AccessPermission.parse(g)),
            scopes: perms.scopes
        }
    }

    protected setActiveSession(value: IActiveSession, save = false) {

        this._active = value;

        apiClient.accessToken = value?.accessToken;

        apiClient.deviceId = this.deviceId;

        if (isNativeApp() && this._active?.accessToken) {
            console.log(nativeApp.getVersion());
            nativeApp.setAccessToken(this._active?.accessToken, this._active?.user?.id);
        }

        if(apiClient.accessToken)
            (save ? localStorage : sessionStorage).setItem("session", JSON.stringify(value));

        this._sessionSaved = save;
    }

    get active() {
        return this._active;
    }

    get deviceId() {
        return localStorage.getItem("deviceId");
    }

    set deviceId(value: Guid) {

        if (value)
            localStorage.setItem("deviceId", value);
        else
            localStorage.removeItem("deviceId");

        apiClient.deviceId = value;
    }
}


export const userSession = new UserSession();

window["closeSession"] = () => userSession.closeAsync(true, true);